/**
 * useProductSearch Hook
 * 
 * Dinamik ürün arama ve filtreleme için custom hook
 * State management, URL sync, cookie yönetimi ve API çağrılarını yönetir
 */

'use client'

import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { AppRouterContext } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { DEFAULT_PAX_COUNT, DEFAULT_INITIAL_DEPARTURE_DAYS_AHEAD } from '@/constants/pricing';
import { getSearchFilters, setSearchFilters, getTravelersPreferences } from '@/lib/cookies';
import { toLocalDateString } from '@/lib/date-utils';
import { addDays } from 'date-fns';

/** product-listing-initial.ts ile aynı varsayılan yat tarihleri (SSR loadKey ile eşleşme) */
function getSsrDefaultYachtDates(): { checkIn: string; checkOut: string } {
    const checkIn = toLocalDateString(addDays(new Date(), 15));
    const checkOutDate = new Date(checkIn + 'T12:00:00');
    checkOutDate.setDate(checkOutDate.getDate() + 7);
    return { checkIn, checkOut: toLocalDateString(checkOutDate) };
}

function resolveYachtDatesForListingKey(
    filtersToUse: Record<string, unknown>,
    travelersPreferences: { departure_date?: string; rooms?: Array<{ adults?: number }> } | null | undefined,
): { checkIn: string; checkOut: string } {
    let checkIn = typeof filtersToUse.check_in_date === 'string' ? filtersToUse.check_in_date : undefined;
    let checkOut = typeof filtersToUse.check_out_date === 'string' ? filtersToUse.check_out_date : undefined;
    if (!checkIn && travelersPreferences?.departure_date) {
        checkIn = travelersPreferences.departure_date;
    }
    if (checkIn && !checkOut) {
        const d = new Date(checkIn + 'T12:00:00');
        d.setDate(d.getDate() + 7);
        checkOut = toLocalDateString(d);
    }
    if (!checkIn || !checkOut) {
        return getSsrDefaultYachtDates();
    }
    return { checkIn, checkOut };
}

// SSR-safe router hook wrapper
function useSafeRouter() {
    const routerContext = useContext(AppRouterContext);
    try {
        return useRouter();
    } catch (error) {
        if (typeof window === 'undefined' || !routerContext) {
            return null;
        }
        throw error;
    }
}

// SSR-safe pathname hook wrapper
function useSafePathname() {
    const routerContext = useContext(AppRouterContext);
    try {
        return usePathname();
    } catch (error) {
        if (typeof window === 'undefined' || !routerContext) {
            return null;
        }
        throw error;
    }
}

// SSR-safe searchParams hook wrapper
function useSafeSearchParams() {
    const routerContext = useContext(AppRouterContext);
    try {
        return useSearchParams();
    } catch (error) {
        if (typeof window === 'undefined' || !routerContext) {
            return null;
        }
        throw error;
    }
}
import { searchProducts, getProductFilters, getCategoryProducts, getProductCategoryBySlug, getAutocompleteSuggestions } from '@/lib/api/product-search';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { useTravelersPreferences } from '@/contexts/TravelersPreferencesContext';
import type {
    Product,
    SearchParams,
    FilterConfig,
    SearchResponse,
    CategoryProductsResponse,
    AutocompleteParams,
    Suggestion,
    ProductListingInitialSsr,
} from '@/types/product-search';

/**
 * Kategori URL’si ile sidebar meta filtresi birlikte uygulanınca kesişim sıfır sonuç verebilir
 * (örn. /yacht/catamaran-… sayfasında sidebar’dan Gulet seçmek). Bu durumda genel arama endpoint’i kullanılır.
 */
function shouldUseGlobalSearchInsteadOfCategoryPage(
    productType: string,
    filters: Record<string, unknown>,
): boolean {
    const yt = filters.yacht_type
    if (productType === 'yacht' && Array.isArray(yt) && yt.length > 0) {
        return true
    }
    // tour_type sidebar’dan kaldırıldı; cookie’de kalsa bile kategori listesi endpoint’i kullanılmalı (aksi halde genel arama + meta kesişimi 0 sonuç)
    return false
}

export interface UseProductSearchOptions {
    categorySlug?: string;
    /** group-tours / private-tours gibi landing sayfasında bu slug ile kategori otomatik seçilir (sidebar’da seçmeden sonuç gelir) */
    landingCategorySlug?: string;
    productType: string;
    language?: string;
    autoLoad?: boolean; // Sayfa yüklendiğinde otomatik ürün çekilsin mi?
    calculatePrices?: boolean; // Fiyat hesaplama aktif mi?
    perPage?: number; // Sayfa başına ürün sayısı
    /** PageRenderer / resolveProps ile sunucuda doldurulan ilk sayfa (page=1, URL filtreleri yoksa kullanılır) */
    initialListing?: ProductListingInitialSsr | null;
}

export interface UseProductSearchReturn {
    // State
    products: Product[];
    filters: FilterConfig | null;
    isLoading: boolean;
    isFiltersLoading: boolean;
    error: string | null;
    
    // Pagination
    pagination: {
        page: number;
        per_page: number;
        total: number;
        last_page: number;
    } | null;
    
    // Category info (eğer kategori sayfasındaysa)
    category: CategoryProductsResponse['category'] | null;
    
    // Filter state
    activeFilters: Record<string, any>;
    sortBy: SearchParams['sort_by'];
    
    // Actions
    updateFilters: (filters: Record<string, any>) => void;
    updateFilter: (fieldKey: string, value: any) => void;
    removeFilter: (fieldKey: string) => void;
    clearFilters: () => void;
    clearSidebarFilters: () => void; // Sadece sidebar filtrelerini temizle
    setSortBy: (sortBy: SearchParams['sort_by']) => void;
    setPage: (page: number) => void;
    refresh: (overrideFilters?: Record<string, unknown>) => Promise<void>;
    clearError: () => void;
    
    // Autocomplete
    getSuggestions: (params: Omit<AutocompleteParams, 'product_type'>) => Promise<Suggestion[]>;
    
    // Travelers preferences (context'ten - oda bazlı)
    travelersPreferences: {
        rooms: Array<{
            adults: number;
            children: number;
            childAges: number[];
        }>;
        departure_date?: string;
    } | null;
}

export function useProductSearch(options: UseProductSearchOptions): UseProductSearchReturn {
    const { getVariable } = useLanguageVariables()
    const { preferences: travelersPreferences } = useTravelersPreferences()
    const {
        categorySlug,
        landingCategorySlug,
        productType,
        language,
        autoLoad = true,
        calculatePrices = true,
        perPage = 12,
        initialListing,
    } = options;

    // Next.js router hooks
    const router = useSafeRouter();
    const pathname = useSafePathname();
    const searchParams = useSafeSearchParams();

    const ssrListingHydrationEligible = (() => {
        const il = initialListing;
        if (!il?.filters || !Array.isArray(il.products)) return false;
        const page = parseInt(searchParams?.get('page') || '1', 10);
        if (page !== 1) return false;
        let hasFilterQuery = false;
        searchParams?.forEach((_, k) => {
            if (k.startsWith('filter_')) hasFilterQuery = true;
        });
        return !hasFilterQuery;
    })();

    // State
    const [products, setProducts] = useState<Product[]>(() =>
        ssrListingHydrationEligible && initialListing ? initialListing.products : []
    );
    const [filters, setFilters] = useState<FilterConfig | null>(() =>
        ssrListingHydrationEligible && initialListing ? initialListing.filters : null
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isFiltersLoading, setIsFiltersLoading] = useState(() => !ssrListingHydrationEligible);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<UseProductSearchReturn['pagination']>(() =>
        ssrListingHydrationEligible && initialListing ? initialListing.pagination : null
    );
    const [category, setCategory] = useState<CategoryProductsResponse['category'] | null>(() =>
        ssrListingHydrationEligible && initialListing ? initialListing.category : null
    );
    
    // Filter state (cookie'den okunur, URL'den fallback)
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
    // Tour / private-tours / group-tours varsayılan: güne göre artan (Days Asc); diğer tipler: created_desc
    const isTourLikeType = productType === 'tour' || productType === 'private-tours' || productType === 'group-tours';
    const defaultSortForType = isTourLikeType ? 'duration_asc' : 'created_desc';
    const [sortBy, setSortByState] = useState<SearchParams['sort_by']>(defaultSortForType);
    
    // Refs for debouncing
    const filtersDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const productsDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Filtreleri oku: Önce URL'den, yoksa cookie'den
    useEffect(() => {
        if (typeof window === 'undefined') return;
        
        const filtersFromUrl: Record<string, any> = {};
        const page = parseInt(searchParams?.get('page') || '1', 10);
        const defaultSort = isTourLikeType ? 'duration_asc' : 'created_desc';
        const sort = (searchParams?.get('sort_by') || defaultSort) as SearchParams['sort_by'];
        
        // URL'ye eklenmeyecek parametreler (cookie'de saklanıyor veya URL'de zaten var)
        const excludedParams = [
            'departure_date',
            'check_in_date',
            'pax_count',
            'child_count',
            'child_ages',
            'destination', // Kategori slug zaten URL'de
        ];
        // Kategori sayfasındayken category_ids her sayfada sadece o sayfanın kategorisiyle sıfırlanacak (cookie/URL'den restore etme)
        if (categorySlug) excludedParams.push('category_ids');
        
        // URL'deki tüm query parametrelerini kontrol et
        let hasUrlFilters = false;
        searchParams?.forEach((value, key) => {
            if (key.startsWith('filter_')) {
                const fieldKey = key.replace('filter_', '');
                if (excludedParams.includes(fieldKey)) return;
                hasUrlFilters = true;
                try {
                    filtersFromUrl[fieldKey] = JSON.parse(value);
                } catch {
                    filtersFromUrl[fieldKey] = value;
                }
            }
        });
        // MainSearchBar'dan station_city seçilince URL'de ?station_city=... gelir; arama sayfasında filtreye ekle
        const urlStationCity = searchParams?.get('station_city');
        if (urlStationCity && typeof urlStationCity === 'string' && urlStationCity.trim() !== '') {
            hasUrlFilters = true;
            filtersFromUrl.station_city = urlStationCity.trim();
        }
        // Tekil "filters" parametresi (JSON objesi, örn. paylaşılan linklerde)
        const filtersParam = searchParams?.get('filters');
        if (filtersParam && typeof filtersParam === 'string') {
            try {
                const parsed = JSON.parse(filtersParam) as Record<string, unknown>;
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    hasUrlFilters = true;
                    Object.entries(parsed).forEach(([k, v]) => {
                        if (!excludedParams.includes(k) && v !== null && v !== undefined && v !== '') {
                            filtersFromUrl[k] = v;
                        }
                    });
                }
            } catch {
                // Geçersiz JSON, yoksay
            }
        }
        
        // URL'de filtre yoksa cookie'den oku
        if (!hasUrlFilters) {
            const filtersFromCookie = getSearchFilters();
            if (filtersFromCookie) {
                // Cookie'deki filtreleri kullan (excluded parametreleri hariç)
                Object.entries(filtersFromCookie).forEach(([key, value]) => {
                    if (!excludedParams.includes(key) && value !== null && value !== undefined && value !== '') {
                        filtersFromUrl[key] = value;
                    }
                });
            }
        }

        // Tur kategori sayfasında tour_type artık kullanılmıyor; cookie’den gelince state’i kirletmesin
        if (categorySlug && isTourLikeType) {
            delete filtersFromUrl.tour_type
        }
        
        setActiveFilters(filtersFromUrl);
        setSortByState(sort);
    }, [searchParams, productType, categorySlug, isTourLikeType]);

    // Kategori sayfasındayken sadece bu sayfanın kategorisi seçili olsun (her sayfa açılışında sıfırlanır, önceki seçimler taşınmaz)
    // Path formatında (örn. private-tours/cappadocia-tours) ise son segment (alt kategori) kullanılır
    useEffect(() => {
        if (!categorySlug || !filters?.sidebar_filters?.length) return;
        const categoryTreeFilter = filters.sidebar_filters.find((f) => f.field_type === 'category_tree' && f.categories?.length);
        if (!categoryTreeFilter?.categories) return;
        const leafSlug = categorySlug.includes('/') ? categorySlug.split('/').pop() || categorySlug : categorySlug;
        type CatNode = { id: number; slug?: string; children?: CatNode[] };
        const findIdBySlug = (nodes: CatNode[]): number | null => {
            for (const node of nodes) {
                if (node.slug === leafSlug || node.slug === categorySlug) return node.id;
                if (node.children?.length) {
                    const found = findIdBySlug(node.children);
                    if (found != null) return found;
                }
            }
            return null;
        };
        const currentCategoryId = findIdBySlug(categoryTreeFilter.categories);
        if (currentCategoryId == null) return;
        setActiveFilters((prev) => ({
            ...prev,
            category_ids: [currentCategoryId],
        }));
    }, [categorySlug, filters?.sidebar_filters]);

    // Landing sayfası (group-tours, private-tours): filtreler yüklenince bu kategorinin id’sini activeFilters’a yaz (sıralama/değişiklikte doğru istek gitsin)
    useEffect(() => {
        if (!landingCategorySlug || !filters?.sidebar_filters?.length) return;
        const categoryTreeFilter = filters.sidebar_filters.find((f) => f.field_type === 'category_tree' && f.categories?.length);
        if (!categoryTreeFilter?.categories) return;
        type CatNode = { id: number; slug?: string; children?: CatNode[] };
        const findIdBySlug = (nodes: CatNode[]): number | null => {
            for (const node of nodes) {
                if (node.slug === landingCategorySlug) return node.id;
                if (node.children?.length) {
                    const found = findIdBySlug(node.children);
                    if (found != null) return found;
                }
            }
            return null;
        };
        const categoryId = findIdBySlug(categoryTreeFilter.categories as CatNode[]);
        if (categoryId == null) return;
        setActiveFilters((prev) => {
            const current = Array.isArray(prev.category_ids) ? prev.category_ids : [];
            if (current.length === 1 && current[0] === categoryId) return prev;
            return { ...prev, category_ids: [categoryId] };
        });
    }, [landingCategorySlug, filters?.sidebar_filters]);

    // Filtre yapılandırmasını çek (categorySlug varsa backend kategoriden ürün tipini çözer, dinamik)
    const loadFilters = useCallback(async () => {
        const typeToUse = productType && productType.trim() ? productType.trim() : 'tour';
        setIsFiltersLoading(true);
        setError(null);
        try {
            const filterConfig = await getProductFilters(typeToUse, language || 'en', categorySlug);
            setFilters(filterConfig);
        } catch (err) {
            setFilters({ search_fields: [], sidebar_filters: [] });
        } finally {
            setIsFiltersLoading(false);
        }
    }, [productType, language, categorySlug]);

    // Ürünleri çek (overrideFilters: kategori sayfasında çift isteği önlemek için kullanılır)
    const loadProducts = useCallback(async (page: number = 1, overrideFilters?: Record<string, unknown>) => {
        if (!productType) {
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        const filtersForRequest = (overrideFilters ?? activeFilters) as Record<string, any>;
        
        try {
            const isYacht = productType === 'yacht';
            
            // Yacht için sadece tarih aralığı (cookie'den veya activeFilters'tan)
            // Tour için: önce travelersPreferences, yoksa URL (departure_date, pax_count), yoksa varsayılan (API'nin hiç çağrılmamasını önlemek için)
            let paxCount: number | undefined;
            let childCount: number | undefined;
            let childAges: number[] | undefined;
            let departureDate: string | undefined;
            let checkInDate: string | undefined;
            let checkOutDate: string | undefined;
            
            if (isYacht) {
                // Yacht: Sadece tarih aralığı (cookie'den veya activeFilters'tan)
                // TravelersPreferences'ta check_in_date ve check_out_date yok, sadece departure_date var
                checkInDate = filtersForRequest.check_in_date || travelersPreferences?.departure_date || undefined;
                checkOutDate = filtersForRequest.check_out_date || undefined;
                
                // Yacht için check_out_date yoksa, check_in_date'den minimum kiralama süresi kadar ekle
                // İlk yüklemede varsayılan 7 gün kullan, ürünler yüklendikten sonra useEffect ile güncellenecek
                if (checkInDate && !checkOutDate) {
                    const checkIn = new Date(checkInDate);
                    // Varsayılan 7 gün (ürünler yüklendikten sonra useEffect ile güncellenecek)
                    checkIn.setDate(checkIn.getDate() + 7);
                    checkOutDate = checkIn.toISOString().split('T')[0];
                }
            } else {
                // Tour: önce istek filtreleri (MainSearchBar'dan gelen override), sonra context (çerez), URL, varsayılan
                const urlDeparture = searchParams?.get('departure_date') ?? undefined;
                const urlPax = searchParams?.get('pax_count');
                const paxFromUrl = urlPax ? parseInt(urlPax, 10) : undefined;
                const validPaxFromUrl = paxFromUrl != null && !isNaN(paxFromUrl) && paxFromUrl > 0 ? paxFromUrl : undefined;
                const defaultDeparture = toLocalDateString(addDays(new Date(), DEFAULT_INITIAL_DEPARTURE_DAYS_AHEAD));
                const fromRequest = filtersForRequest as Record<string, unknown>;
                const requestDeparture = typeof fromRequest.departure_date === 'string' ? fromRequest.departure_date : undefined;
                const requestPax = typeof fromRequest.pax_count === 'number' ? fromRequest.pax_count : (typeof fromRequest.pax_count === 'string' ? parseInt(String(fromRequest.pax_count), 10) : undefined);
                const validRequestPax = requestPax != null && !isNaN(requestPax) && requestPax > 0 ? requestPax : undefined;
                const requestChildCount = typeof fromRequest.child_count === 'number' ? fromRequest.child_count : undefined;
                const requestChildAges = Array.isArray(fromRequest.child_ages) ? fromRequest.child_ages as number[] : (typeof fromRequest.child_ages === 'string' ? (() => { try { const a = JSON.parse(fromRequest.child_ages as string); return Array.isArray(a) ? a : undefined; } catch { return undefined; } })() : undefined);
                const cookiePrefs = typeof window !== 'undefined' ? getTravelersPreferences() : null;
                const firstRoom = travelersPreferences?.rooms?.[0] ?? cookiePrefs?.rooms?.[0];
                paxCount = validRequestPax ?? (firstRoom?.adults && firstRoom.adults > 0 ? firstRoom.adults : undefined)
                    ?? validPaxFromUrl ?? (calculatePrices ? DEFAULT_PAX_COUNT : undefined);
                childCount = requestChildCount ?? (firstRoom?.children && firstRoom.children > 0 ? firstRoom.children : undefined);
                departureDate = requestDeparture ?? travelersPreferences?.departure_date ?? cookiePrefs?.departure_date ?? urlDeparture ?? (calculatePrices ? defaultDeparture : undefined);
                childAges = (requestChildAges && requestChildAges.length > 0 ? requestChildAges : undefined) ?? (firstRoom?.childAges && firstRoom.childAges.length > 0 ? firstRoom.childAges : undefined);
            }
            
            const tourLikeListing =
                productType === 'tour' || productType === 'private-tours' || productType === 'group-tours'

            // Varsayılan (tam) aralıktaki range filtrelerini API'ye gönderme; böylece backend
            // gereksiz whereHas uygulamaz ve tour_duration meta'sı olmayan ürünler elenmez.
            const filtersToSend = ((): Record<string, unknown> => {
                const f = { ...filtersForRequest } as Record<string, unknown>;
                // Kategori sayfasında eski cookie tour_type, ürün meta’sı ile AND’lenip liste boşalıyordu (filtre artık yok)
                if (categorySlug && tourLikeListing) {
                    delete f.tour_type
                }
                if (filters?.sidebar_filters) {
                    for (const sf of filters.sidebar_filters) {
                        if (sf.field_type === 'range' && sf.min != null && sf.max != null) {
                            const v = f[sf.field_key];
                            if (v && typeof v === 'object' && v !== null && 'min' in v && 'max' in v) {
                                const vMin = Number((v as { min: unknown }).min);
                                const vMax = Number((v as { max: unknown }).max);
                                if (vMin === Number(sf.min) && vMax === Number(sf.max)) {
                                    delete f[sf.field_key];
                                }
                            }
                        }
                    }
                }
                return f;
            })();

            const params: SearchParams = {
                product_type: productType,
                language,
                page,
                per_page: perPage,
                filters: filtersToSend as SearchParams['filters'],
                sort_by: sortBy,
                calculate_prices: calculatePrices,
                // Yacht için pax_count, child_count, child_ages gönderme
                pax_count: isYacht ? undefined : paxCount,
                departure_date: isYacht ? undefined : departureDate,
                check_in_date: isYacht ? checkInDate : undefined,
                check_out_date: isYacht ? checkOutDate : undefined,
                child_count: isYacht ? undefined : (childCount && childCount > 0 ? childCount : undefined),
                child_ages: isYacht ? undefined : childAges,
            };
            
            const useCategoryListingEndpoint =
                Boolean(categorySlug) &&
                !shouldUseGlobalSearchInsteadOfCategoryPage(productType, filtersForRequest as Record<string, unknown>)

            if (useCategoryListingEndpoint) {
                // Kategori sayfası: getCategoryProducts kullan
                const categoryResponse = await getCategoryProducts(categorySlug!, {
                    product_type: productType,
                    language,
                    page,
                    per_page: perPage,
                    filters: filtersToSend as SearchParams['filters'],
                    sort_by: sortBy,
                    calculate_prices: calculatePrices,
                    departure_date: isYacht ? undefined : departureDate,
                    check_in_date: isYacht ? checkInDate : undefined,
                    check_out_date: isYacht ? checkOutDate : undefined,
                    pax_count: isYacht ? undefined : paxCount,
                    child_count: isYacht ? undefined : (childCount && childCount > 0 ? childCount : undefined),
                    child_ages: isYacht ? undefined : childAges,
                });
                
                // Category bilgisini set et
                if (categoryResponse.category) {
                    setCategory(categoryResponse.category);
                }
                
                // Products ve pagination'ı set et
                if (categoryResponse.success) {
                    const productsData = Array.isArray(categoryResponse.data) ? categoryResponse.data : [];
                    setProducts(productsData);
                    
                    if (categoryResponse.meta?.pagination) {
                        setPagination(categoryResponse.meta.pagination);
                    } else {
                        setPagination({
                            page: page,
                            per_page: perPage,
                            total: productsData.length,
                            last_page: 1,
                        });
                    }
                } else {
                    setProducts([]);
                    setPagination(null);
                }
            } else {
                // Kategori URL’si varken genel aramaya düşüldüyse (tip filtresi vb.), eski kategori başlığını gösterme
                if (categorySlug) {
                    setCategory(null)
                } else if (landingCategorySlug) {
                    // Landing sayfası (group-tours / private-tours): liste searchProducts ile; açıklama slug ile ayrı çekilir
                    const landingCategory = await getProductCategoryBySlug(landingCategorySlug, language || 'en');
                    setCategory(landingCategory);
                } else {
                    setCategory(null);
                }
                // Genel arama: searchProducts kullan
                const searchResponse = await searchProducts(params);
                
                // Products ve pagination'ı set et
                if (searchResponse.success) {
                    const productsData = Array.isArray(searchResponse.data) ? searchResponse.data : [];
                    setProducts(productsData);
                    
                    if (searchResponse.meta?.pagination) {
                        setPagination(searchResponse.meta.pagination);
                    } else {
                        setPagination({
                            page: page,
                            per_page: perPage,
                            total: productsData.length,
                            last_page: 1,
                        });
                    }
                } else {
                    setProducts([]);
                    setPagination(null);
                }
            }
        } catch (err) {
            // Error durumunda products'ı boş array yap
            setProducts([]);
            // Error'u parse et ve kullanıcı dostu mesaja çevir
            let userFriendlyMessage = getVariable('error_loading_products', 'An error occurred while loading products. Please try again later.');
            
            if (err instanceof Error) {
                const errorMessage = err.message;
                
                // 404 Not Found hatası
                if (errorMessage.includes('404') || errorMessage.includes('Not Found') || errorMessage.includes('bulunamadı')) {
                    if (categorySlug) {
                        userFriendlyMessage = getVariable('error_no_products_category', 'No products found for this category. Please select a different category or change the filters.');
                    } else {
                        userFriendlyMessage = getVariable('error_no_products_criteria', 'No products found matching your criteria. Please change the filters and try again.');
                    }
                }
                // 403 Forbidden hatası
                else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
                    userFriendlyMessage = getVariable('error_no_permission', 'You do not have permission for this operation. Please refresh the page.');
                }
                // 500 Server Error
                else if (errorMessage.includes('500') || errorMessage.includes('Server Error')) {
                    userFriendlyMessage = getVariable('error_server_error', 'A server error occurred. Please try again in a few minutes.');
                }
                // Network hatası
                else if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('Network')) {
                    userFriendlyMessage = getVariable('error_network', 'Please check your internet connection and refresh the page.');
                }
                // Backend'den gelen Türkçe mesaj varsa onu kullan
                else if (errorMessage.includes('Ürün') || errorMessage.includes('Kategori') || errorMessage.includes('Filtre')) {
                    // JSON içindeki mesajı çıkar
                    try {
                        const jsonMatch = errorMessage.match(/\{[^}]+\}/);
                        if (jsonMatch) {
                            const errorData = JSON.parse(jsonMatch[0]);
                            if (errorData.error && typeof errorData.error === 'string') {
                                userFriendlyMessage = errorData.error;
                            } else if (errorData.message && typeof errorData.message === 'string') {
                                userFriendlyMessage = errorData.message;
                            }
                        }
                    } catch {
                        // JSON parse edilemezse, errorMessage'ın kendisini kullan
                        if (errorMessage.length < 100) {
                            userFriendlyMessage = errorMessage;
                        }
                    }
                }
            }
            
            setError(userFriendlyMessage);
        } finally {
            setIsLoading(false);
        }
    }, [productType, categorySlug, landingCategorySlug, language, perPage, activeFilters, sortBy, calculatePrices, getVariable, travelersPreferences, filters, searchParams]);

    // Filtreleri güncelle - Cookie'ye yaz
    const updateFilters = useCallback((newFilters: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => {
        setActiveFilters((prevFilters) => {
            const resolvedFilters = typeof newFilters === 'function' ? newFilters(prevFilters) : newFilters;
            
            // Cookie'ye kaydet (URL'ye yazmıyoruz, güvenlik nedeniyle)
            const excludedParams = [
                'departure_date',
                'check_in_date',
                'pax_count',
                'child_count',
                'child_ages',
                'destination',
            ];
            
            const filtersToSave: Record<string, any> = {};
            Object.entries(resolvedFilters).forEach(([key, value]) => {
                if (!excludedParams.includes(key) && value !== null && value !== undefined && value !== '') {
                    filtersToSave[key] = value;
                }
            });
            
            // Cookie'ye kaydet
            setSearchFilters(filtersToSave);
            
            return resolvedFilters;
        });
    }, []);

    // Tek bir filtreyi güncelle - functional update pattern ile optimize edilmiş
    const updateFilter = useCallback((fieldKey: string, value: any) => {
        updateFilters((prevFilters) => ({ ...prevFilters, [fieldKey]: value }));
    }, [updateFilters]);

    // Filtreyi kaldır - functional update pattern ile optimize edilmiş
    const removeFilter = useCallback((fieldKey: string) => {
        updateFilters((prevFilters) => {
            const newFilters = { ...prevFilters };
            delete newFilters[fieldKey];
            return newFilters;
        });
    }, [updateFilters]);

    // Tüm filtreleri temizle
    const clearFilters = useCallback(() => {
        updateFilters({});
    }, [updateFilters]);

    // Sadece sidebar filtrelerini temizle (MainSearchBar'daki alanları koru)
    const clearSidebarFilters = useCallback(() => {
        // MainSearchBar'daki alanları koru (bunlar cookie'de saklanıyor)
        const excludedParams = [
            'departure_date',
            'check_in_date',
            'pax_count',
            'child_count',
            'child_ages',
            'destination', // Kategori slug zaten URL'de
        ];
        
        // Sadece sidebar filtrelerini temizle
        updateFilters((prevFilters) => {
            const newFilters: Record<string, any> = {};
            
            // Excluded parametreleri koru
            excludedParams.forEach((key) => {
                if (prevFilters[key] !== undefined && prevFilters[key] !== null && prevFilters[key] !== '') {
                    newFilters[key] = prevFilters[key];
                }
            });
            
            return newFilters;
        });
    }, [updateFilters]);

    // Yacht için: Ürünler yüklendikten sonra minimum kiralama sürelerine göre tarih aralığını güncelle
    useEffect(() => {
        if (productType !== 'yacht' || products.length === 0 || !calculatePrices) return;
        
        // Minimum kiralama sürelerini topla
        const minRentalDurations = products
            .map(p => {
                // calculated_price'dan min_rental_duration al
                const calcMinRental = (p.calculated_price as any)?.min_rental_duration;
                if (calcMinRental && calcMinRental > 0) return calcMinRental;
                
                // metas'tan min_rental_duration al
                const metaMinRental = p.metas?.min_rental_duration;
                if (metaMinRental) {
                    const parsed = parseInt(String(metaMinRental));
                    if (!isNaN(parsed) && parsed > 0) return parsed;
                }
                
                return null;
            })
            .filter((d): d is number => d !== null && d > 0);
        
        if (minRentalDurations.length === 0) return;
        
        // En yaygın minimum kiralama süresini bul (mod)
        const frequency: Record<number, number> = {};
        minRentalDurations.forEach(d => {
            frequency[d] = (frequency[d] || 0) + 1;
        });
        const mostCommon = Object.entries(frequency).sort((a, b) => b[1] - a[1])[0];
        const avgMinRental = mostCommon 
            ? parseInt(mostCommon[0])
            : Math.round(minRentalDurations.reduce((a, b) => a + b, 0) / minRentalDurations.length);
        
        // Mevcut tarih aralığını kontrol et
        // TravelersPreferences'ta check_in_date ve check_out_date yok, sadece departure_date var
        const currentCheckIn = activeFilters.check_in_date || travelersPreferences?.departure_date;
        const currentCheckOut = activeFilters.check_out_date;
        
        // Eğer tarih aralığı yoksa veya kiralama süresi minimum kiralama süresinden küçükse güncelle
        if (currentCheckIn) {
            const checkInDate = new Date(currentCheckIn);
            let needsUpdate = false;
            let newCheckOutDate: Date | null = null;
            
            if (!currentCheckOut) {
                // check_out_date yoksa, minimum kiralama süresine göre ekle
                needsUpdate = true;
                newCheckOutDate = new Date(checkInDate);
                newCheckOutDate.setDate(newCheckOutDate.getDate() + avgMinRental);
            } else {
                // check_out_date varsa, kiralama süresini kontrol et
                const checkOutDate = new Date(currentCheckOut);
                const diffDays = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
                
                if (diffDays < avgMinRental) {
                    // Kiralama süresi minimum kiralama süresinden küçükse güncelle
                    needsUpdate = true;
                    newCheckOutDate = new Date(checkInDate);
                    newCheckOutDate.setDate(newCheckOutDate.getDate() + avgMinRental);
                }
            }
            
            if (needsUpdate && newCheckOutDate) {
                const newCheckOutStr = toLocalDateString(newCheckOutDate);
                updateFilters((prev) => ({
                    ...prev,
                    check_out_date: newCheckOutStr,
                }));
                
                // Cookie'ye de kaydet (searchFilters kullan)
                const currentSearchFilters = getSearchFilters();
                setSearchFilters({
                    ...currentSearchFilters,
                    check_out_date: newCheckOutStr,
                });
            }
        }
    }, [products, productType, calculatePrices, activeFilters, travelersPreferences, updateFilters]);

    // Sıralamayı güncelle - Cookie'ye yaz
    const setSortBy = useCallback((newSortBy: SearchParams['sort_by']) => {
        setSortByState(newSortBy);
        // Sıralama cookie'ye yazılmıyor, sadece state'te tutuluyor
    }, []);

    // Sayfa değiştir - URL'ye yaz (sayfa numarası URL'de kalabilir)
    const setPage = useCallback((page: number) => {
        if (typeof window === 'undefined' || !router || !pathname) return;
        
        setTimeout(() => {
            const params = new URLSearchParams();
            if (page > 1) {
                params.set('page', String(page));
            }
            const newUrl = `${pathname}${params.toString() ? `?${params.toString()}` : ''}`;
            router.replace(newUrl, { scroll: false });
        }, 0);
    }, [router, pathname]);

    // Yenile (mevcut filtrelerle tekrar yükle). overrideFilters verilirse MainSearchBar'dan gelen filtrelerle hemen istek atar (state güncellenmeden).
    const refresh = useCallback(async (overrideFilters?: Record<string, unknown>) => {
        // loadKey'i sıfırla ki yeniden yüklensin
        productsLoadedRef.current = '';
        const currentPage = parseInt(searchParams?.get('page') || '1', 10);
        const filtersToUse = overrideFilters ?? activeFilters;
        await loadProducts(currentPage, overrideFilters ?? undefined);
        
        // loadKey'i güncelle (yükleme tamamlandıktan sonra)
        const isYacht = productType === 'yacht';
        let loadKey: string;
        if (isYacht) {
            const checkIn = (filtersToUse as Record<string, unknown>).check_in_date || '';
            const checkOut = (filtersToUse as Record<string, unknown>).check_out_date || '';
            loadKey = `${productType}-${categorySlug || ''}-${currentPage}-${JSON.stringify(filtersToUse)}-${sortBy}-checkin${checkIn}-checkout${checkOut}`;
        } else {
            const paxFromOverride = (filtersToUse as Record<string, unknown>).pax_count;
            const paxCount = typeof paxFromOverride === 'number' && paxFromOverride > 0 ? paxFromOverride : (travelersPreferences?.rooms?.[0]?.adults && travelersPreferences.rooms[0].adults > 0 ? travelersPreferences.rooms[0].adults : DEFAULT_PAX_COUNT);
            loadKey = `${productType}-${categorySlug || ''}-${currentPage}-${JSON.stringify(filtersToUse)}-${sortBy}-pax${paxCount}`;
        }
        productsLoadedRef.current = loadKey;
    }, [loadProducts, searchParams, productType, categorySlug, activeFilters, sortBy, travelersPreferences]);

    // Error'u temizle
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Autocomplete önerileri
    const getSuggestions = useCallback(async (params: Omit<AutocompleteParams, 'product_type'>): Promise<Suggestion[]> => {
        try {
            return await getAutocompleteSuggestions({
                ...params,
                product_type: productType,
            });
        } catch (err) {
            return [];
        }
    }, [productType]);

    const filtersLoadedRef = useRef(!!(ssrListingHydrationEligible && initialListing?.filters));
    useEffect(() => {
        if (filtersLoadedRef.current) return;
        filtersLoadedRef.current = true;
        loadFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productType, language]);

    // productType veya language değişince filtreleri yeniden yükle
    useEffect(() => {
        filtersLoadedRef.current = false;
    }, [productType, language]);

    const productsLoadedRef = useRef<string>('');
    const skipDuplicateListingFetchRef = useRef(!!ssrListingHydrationEligible);

    // Kategori sayfasında category_ids filtreleri yüklenene kadar bekle; böylece tek istek atılır (çift istek önlenir)
    const getEffectiveFiltersForCategory = useCallback((): Record<string, unknown> | null => {
        if (!categorySlug || !filters?.sidebar_filters?.length) return null;
        const categoryTreeFilter = filters.sidebar_filters.find((f: { field_type?: string; categories?: unknown[] }) => f.field_type === 'category_tree' && f.categories?.length);
        if (!categoryTreeFilter?.categories) return null;
        const leafSlug = categorySlug.includes('/') ? categorySlug.split('/').pop() || categorySlug : categorySlug;
        type CatNode = { id: number; slug?: string; children?: CatNode[] };
        const findIdBySlug = (nodes: CatNode[]): number | null => {
            for (const node of nodes) {
                if (node.slug === leafSlug || node.slug === categorySlug) return node.id;
                if (node.children?.length) {
                    const found = findIdBySlug(node.children);
                    if (found != null) return found;
                }
            }
            return null;
        };
        const currentCategoryId = findIdBySlug(categoryTreeFilter.categories as CatNode[]);
        // Ağaçta slug yoksa cookie’deki category_ids yanlış OR ile 0 sonuç verir; URL slug’ına göre backend byCategory kullansın
        if (currentCategoryId == null) {
            const { category_ids: _omit, ...rest } = activeFilters
            return { ...rest } as Record<string, unknown>
        }
        // Sidebar’dan seçilen category_ids varsa ezme; yoksa URL’deki kategori sayfasına kilitle
        const fromSidebar = Array.isArray(activeFilters.category_ids) && activeFilters.category_ids.length > 0
        const categoryIds = fromSidebar ? activeFilters.category_ids : [currentCategoryId]
        return { ...activeFilters, category_ids: categoryIds } as Record<string, unknown>
    }, [categorySlug, filters?.sidebar_filters, activeFilters]);

    // Landing sayfası (group-tours, private-tours): slug ile kategori id bulunup filtreye eklenir; sayfa açılınca sonuç gelir
    const getEffectiveFiltersForLanding = useCallback((): Record<string, unknown> | null => {
        if (!landingCategorySlug || !filters?.sidebar_filters?.length) return null;
        const categoryTreeFilter = filters.sidebar_filters.find((f: { field_type?: string; categories?: unknown[] }) => f.field_type === 'category_tree' && f.categories?.length);
        if (!categoryTreeFilter?.categories) return null;
        type CatNode = { id: number; slug?: string; children?: CatNode[] };
        const findIdBySlug = (nodes: CatNode[]): number | null => {
            for (const node of nodes) {
                if (node.slug === landingCategorySlug) return node.id;
                if (node.children?.length) {
                    const found = findIdBySlug(node.children);
                    if (found != null) return found;
                }
            }
            return null;
        };
        const categoryId = findIdBySlug(categoryTreeFilter.categories as CatNode[]);
        if (categoryId == null) return null;
        const fromSidebar = Array.isArray(activeFilters.category_ids) && activeFilters.category_ids.length > 0
        const categoryIds = fromSidebar ? activeFilters.category_ids : [categoryId]
        return { ...activeFilters, category_ids: categoryIds } as Record<string, unknown>
    }, [landingCategorySlug, filters?.sidebar_filters, activeFilters]);

    useEffect(() => {
        // categorySlug yokken de çalışır: group-tours / private-tours gibi ürün tipi landing sayfaları genel arama (searchProducts) ile yüklenir
        if (!autoLoad || !productType) return;

        // travelersPreferences null olsa bile API çağrılıyor (loadProducts içinde URL/varsayılan departure_date ve pax_count kullanılıyor)

        // Kategori veya landing sayfasında filtre yüklemesi bitene kadar bekle (category_ids çözülebilsin)
        if ((categorySlug || landingCategorySlug) && filters === null) return;

        // Kategori sayfası: effective filtre; landing sayfası: landing slug’a göre category_ids; yoksa activeFilters
        const effectiveFilters = categorySlug ? getEffectiveFiltersForCategory() : getEffectiveFiltersForLanding();
        const filtersToUse: Record<string, unknown> = effectiveFilters ?? activeFilters;

        const currentPage = parseInt(searchParams?.get('page') || '1', 10);
        const urlPax = searchParams?.get('pax_count');
        const paxFromUrl = urlPax ? parseInt(urlPax, 10) : undefined;
        const validUrlPax = paxFromUrl != null && !isNaN(paxFromUrl) && paxFromUrl > 0 ? paxFromUrl : undefined;
        const firstRoom = travelersPreferences?.rooms?.[0];
        const paxCount = (firstRoom?.adults && firstRoom.adults > 0 ? firstRoom.adults : undefined) ?? validUrlPax ?? DEFAULT_PAX_COUNT;
        const filtersStr = JSON.stringify(filtersToUse);
        const isTourLike =
            productType === 'tour' || productType === 'private-tours' || productType === 'group-tours';
        const loadKey =
            productType === 'yacht'
                ? (() => {
                      const { checkIn, checkOut } = resolveYachtDatesForListingKey(filtersToUse, travelersPreferences);
                      return `${productType}-${categorySlug ?? landingCategorySlug}-${currentPage}-${filtersStr}-${sortBy}-checkin${checkIn}-checkout${checkOut}`;
                  })()
                : `${productType}-${categorySlug ?? landingCategorySlug}-${currentPage}-${filtersStr}-${sortBy}-pax${paxCount}`;

        if (productsLoadedRef.current === loadKey) return;

        if (skipDuplicateListingFetchRef.current && initialListing) {
            skipDuplicateListingFetchRef.current = false;
            // SSR ile ref hizası: tour-like → DEFAULT_PAX_COUNT; yacht → listing-initial varsayılan tarihler
            let ssrMatchKey: string;
            if (isTourLike) {
                ssrMatchKey = `${productType}-${categorySlug ?? landingCategorySlug}-${currentPage}-${filtersStr}-${sortBy}-pax${DEFAULT_PAX_COUNT}`;
            } else if (productType === 'yacht') {
                const def = getSsrDefaultYachtDates();
                ssrMatchKey = `${productType}-${categorySlug ?? landingCategorySlug}-${currentPage}-${filtersStr}-${sortBy}-checkin${def.checkIn}-checkout${def.checkOut}`;
            } else {
                ssrMatchKey = loadKey;
            }
            productsLoadedRef.current = ssrMatchKey;
            return undefined;
        }

        if (productsDebounceRef.current) {
            clearTimeout(productsDebounceRef.current);
        }

        const debounceMs = 50;

        productsDebounceRef.current = setTimeout(() => {
            loadProducts(currentPage, filtersToUse).then(() => {
                productsLoadedRef.current = loadKey;
            });
        }, debounceMs);

        return () => {
            if (productsDebounceRef.current) {
                clearTimeout(productsDebounceRef.current);
            }
        };
    }, [productType, categorySlug, landingCategorySlug, activeFilters, sortBy, autoLoad, loadProducts, searchParams, calculatePrices, travelersPreferences, getEffectiveFiltersForCategory, getEffectiveFiltersForLanding, filters, initialListing]);


    return {
        // State
        products,
        filters,
        isLoading,
        isFiltersLoading,
        error,
        pagination,
        category,
        activeFilters,
        sortBy,
        travelersPreferences,
        
        // Actions
        updateFilters,
        updateFilter,
        removeFilter,
        clearFilters,
        clearSidebarFilters,
        setSortBy,
        setPage,
        refresh,
        clearError,
        getSuggestions,
    };
}
